import * as XLSX from "xlsx";

// Mérito import for the STEP I&D&I notices (MPr-2026-9, COMPETE2030-2026-11).
//
// The workbook ("Apuramento Despesa I&D&I") is read into plain data
// (readMeritoWorkbook) and merged into the form (applyMerito). The merge is
// pure, so the modal runs it on every change to show what will be created or
// updated, and runs it again on confirm.
//
// The form's activities are the work packages (WP) of the Gantt (the AUX sheet
// is not used); their tasks give the dates and the maturity, and group the
// personnel ETI, which are summed per WP. WPs are matched by their number
// ("WP2 - …" and "WP2. …" are the same WP), other names by designation.
//
// Rules shared with the ROBERTO import: the file overrides the form. Activities
// are matched by designation and technicians by name (per beneficiary),
// ignoring accents, case and repeated spaces; a matched row keeps its id and the
// fields that do not come from the file, so related data does not have to be
// filled in again. What is not in the file is deleted: activities and CAE, and
// the technicians, team rows and cost rows of the imported beneficiaries. As in
// ROBERTO, team rows of deleted technicians and the imported beneficiaries' team
// rows of deleted activities go with them; other rows that point to a deleted
// activity (other beneficiaries' team rows, costs, ...) are left without it. Establishments
// (projectLocation) are not imported: the existing ones are used.

export const MERITO_SECTIONS = [
  { key: "schedule", label: "Calendarização (datas de início e fim)" },
  { key: "cae", label: "CAE do projeto" },
  { key: "activities", label: "Atividades (WP)" },
  { key: "technicians", label: "Pessoal técnico" },
  { key: "team", label: "Equipa (ETI por atividade)" },
  { key: "costs", label: "Custos (Quadro de Investimentos)" },
  { key: "financing", label: "Financiamento" },
];

const CONDITIONS_SHEET = "Condições Validação I&D";
const GANTT_SHEET = "Gantt";
const COSTS_SHEET = "Quadro Invest.";
const FINANCING_SHEET = "Financiamento";
const MAX_ENTITIES = 17; // columns O..AE of the conditions sheet
const MAX_TECHNICIANS = 20; // blocks of 6 columns per personnel sheet
const PERSONNEL_ROWS = [12, 68]; // activity rows of the personnel sheets
const PERSONNEL_YEARS = 5;
const FINANCING_ROWS = 20;
// Editable rows of the financing table (17_Financing.js); the others are formulas.
const FINANCING_INPUT_ROWS = [1, 2, 3, 4, 5, 7, 8, 10, 11, 12, 13, 14, 16, 17];
const STAFF_RUBRIC = "pessoal tecnico do beneficiario";
const TRAVEL_RUBRIC = "13";
const STAY_RUBRIC = "14";
const HIRE_BOND = "17"; // "A contratar"

// ---------------------------------------------------------------- helpers

export const normText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// Entity names are compared across sheets (Condições, CPessoal B8, Quadro
// Invest., Financiamento): also ignore punctuation and special characters, so
// "STREAM Consulting, Lda." and "STREAM Consulting Lda" match.
const entityKey = (value) =>
  normText(value).replace(/[^a-z0-9]+/g, " ").trim();

const colName = (index) => XLSX.utils.encode_col(index);

const rawValue = (ws, address) => {
  const cell = ws?.[address];
  if (!cell || cell.t === "e") return null;
  return cell.v ?? null;
};

const text = (ws, address) => {
  const value = rawValue(ws, address);
  return value == null ? "" : String(value).trim();
};

const number = (ws, address) => {
  const value = rawValue(ws, address);
  if (value == null || value === "") return null;
  // Text cells use the PT format: "12.500,00" (thousands ".", decimals ",").
  const str = String(value).replace(/\s/g, "");
  const parsed =
    typeof value === "number"
      ? value
      : Number(str.includes(",") ? str.replace(/\./g, "").replace(",", ".") : str);
  return Number.isFinite(parsed) ? parsed : null;
};

const pad = (value) => String(value).padStart(2, "0");

// { y, m, d } from a date cell (serial number, Date or "dd-mm-aaaa"/"aaaa").
const dateParts = (ws, address) => {
  const value = rawValue(ws, address);
  if (value instanceof Date) {
    return { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() };
  }
  if (typeof value === "number") {
    if (value >= 1900 && value <= 2100) return { y: value, m: null, d: null };
    const parsed = XLSX.SSF.parse_date_code(value);
    return parsed ? { y: parsed.y, m: parsed.m, d: parsed.d } : null;
  }
  const match = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(String(value ?? "").trim());
  if (match) return { y: +match[3], m: +match[2], d: +match[1] };
  const year = /^(\d{4})$/.exec(String(value ?? "").trim());
  return year ? { y: +year[1], m: null, d: null } : null;
};

const isoDate = (parts) =>
  parts?.y && parts?.m && parts?.d ? `${parts.y}-${pad(parts.m)}-${pad(parts.d)}` : "";

// Gantt end dates are the first day of the last month: use that month's last day.
const endOfMonth = (parts) => {
  if (!parts?.y || !parts?.m) return "";
  const last = new Date(Date.UTC(parts.y, parts.m, 0)).getUTCDate();
  return `${parts.y}-${pad(parts.m)}-${pad(last)}`;
};

const round = (value, digits = 2) =>
  value == null ? null : Math.round(value * 10 ** digits) / 10 ** digits;

const nextId = (rows) =>
  (rows ?? []).reduce((max, row) => Math.max(max, Number(row?.id) || 0), 0) + 1;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// Option value whose label matches (ignoring accents/case and "a.1 "-like prefixes).
const optionValue = (options, label) => {
  const target = normText(label);
  if (!target) return "";
  const strip = (value) => normText(value).replace(/^[a-z]\.\d+\s+/, "");
  const found = (options ?? []).find(
    (option) =>
      normText(option.label) === target || strip(option.label) === strip(label),
  );
  return found ? found.value : "";
};

// ---------------------------------------------------------------- reading

const readEntities = (wb) => {
  const ws = wb.Sheets[CONDITIONS_SHEET];
  const entities = [];
  for (let index = 0; index < MAX_ENTITIES; index++) {
    const col = colName(14 + index); // O
    const name = text(ws, `${col}10`);
    if (!name || name === "0") continue;
    entities.push({
      index,
      name,
      type: text(ws, `${col}6`),
      address: text(ws, `${col}8`).replace(/\s*\n\s*/g, ", "),
    });
  }
  return entities;
};

const readSchedule = (wb) => {
  const ws = wb.Sheets[CONDITIONS_SHEET];
  return {
    startDate: isoDate(dateParts(ws, "D6")),
    endDate: isoDate(dateParts(ws, "D10")),
  };
};

const readCaes = (wb) => {
  const ws = wb.Sheets[CONDITIONS_SHEET];
  const caes = [];
  for (let row = 33; row <= 45; row++) {
    const code = text(ws, `D${row}`);
    if (!code) continue;
    caes.push({ cae: code.replace(/\D/g, "").padStart(5, "0"), percent: number(ws, `C${row}`) });
  }
  return caes;
};

// "WP2 - Preliminary design" and "WP2. Aeronave" -> "wp2"; other names as is.
export const activityKey = (name) => {
  const match = /^wp\s*0*(\d+)/.exec(normText(name));
  return match ? `wp${match[1]}` : normText(name);
};

// Gantt tasks (rows with a TRL in C) and the WP header (no TRL) they are under.
const readGanttTasks = (wb) => {
  const ws = wb.Sheets[GANTT_SHEET];
  if (!ws?.["!ref"]) return [];
  const last = XLSX.utils.decode_range(ws["!ref"]).e.r + 1;
  const tasks = [];
  let workPackage = "";
  for (let row = 6; row <= last; row++) {
    const designation = text(ws, `E${row}`);
    const maturity = text(ws, `C${row}`);
    if (designation && !maturity) workPackage = designation; // WP header row
    if (!designation || !maturity) continue;
    tasks.push({
      designation,
      maturity,
      workPackage,
      startDate: isoDate(dateParts(ws, `BI${row}`)),
      endDate: endOfMonth(dateParts(ws, `BJ${row}`)),
      innovation: text(ws, `BK${row}`),
    });
  }
  return tasks;
};

// Activities = WP headers of the Gantt, dated by their tasks (earliest start,
// latest end). The maturity is the most frequent TRL of the tasks; a WP whose
// tasks are all "Inovação" is an innovation activity with their innovation type.
const readWorkPackages = (tasks) => {
  const groups = new Map();
  tasks.forEach((task) => {
    if (!task.workPackage) return;
    const group = groups.get(task.workPackage) ?? [];
    group.push(task);
    groups.set(task.workPackage, group);
  });
  return [...groups.entries()].map(([designation, own]) => {
    const counts = new Map();
    own.forEach(({ maturity }) => {
      const trl = maturity.replace(/\s/g, "");
      if (trl === "[3-4]" || trl === "[5-9]") counts.set(trl, (counts.get(trl) ?? 0) + 1);
    });
    // Ties keep the first TRL of the WP (Map keeps insertion order).
    const trl = [...counts.entries()].reduce(
      (best, entry) => (entry[1] > (best?.[1] ?? 0) ? entry : best),
      null,
    )?.[0];
    const isInnovation = own.every((task) => normText(task.maturity) === "inovacao");
    const starts = own.map((task) => task.startDate).filter(Boolean).sort();
    const ends = own.map((task) => task.endDate).filter(Boolean).sort();
    return {
      designation,
      maturity: isInnovation ? "Inovação" : trl ?? "",
      startDate: starts[0] ?? "",
      endDate: ends[ends.length - 1] ?? "",
      innovation: isInnovation ? own.find((task) => task.innovation)?.innovation ?? "" : "",
    };
  });
};

// Personnel sheet of an entity: the company is in B8 (merged B8:C11); falls
// back to the sheet order ("CPessoal Líder", "CPessoal Co2", ...).
const personnelSheet = (wb, entity) => {
  const byCompany = wb.SheetNames.find(
    (name) =>
      name.startsWith("CPessoal") &&
      entityKey(text(wb.Sheets[name], "B8")) === entityKey(entity.name),
  );
  return wb.Sheets[
    byCompany ?? (entity.index === 0 ? "CPessoal Líder" : `CPessoal Co${entity.index + 1}`)
  ];
};

// One block of 6 columns per technician: content, then the ETI per year. The
// header rows hold bond (row 7, first year column), level (+2), critical (+3),
// base salary (row 9, +1) and SS rate (row 9, +4). The leader's sheet starts at
// column J (TRL in C); the co-promoters' sheets are one column to the right, so
// the positions are taken from the "Vínculo" (row 6) and "TRL" (row 8) headers.
const findColumn = (ws, row, label, from = 0, to = 40) => {
  for (let index = from; index <= to; index++) {
    if (normText(text(ws, `${colName(index)}${row}`)) === normText(label)) return index;
  }
  return null;
};

const readTechnicians = (wb, entity, taskWorkPackage) => {
  const ws = personnelSheet(wb, entity);
  if (!ws) return [];
  const firstBond = findColumn(ws, 6, "Vínculo");
  const trlColumn = colName(findColumn(ws, 8, "TRL") ?? 2);
  if (firstBond == null) return [];
  const technicians = [];
  for (let slot = 0; slot < MAX_TECHNICIANS; slot++) {
    const base = firstBond - 1 + slot * 6; // content column
    const at = (offset, row) => `${colName(base + offset)}${row}`;
    const name = text(ws, at(1, 8));
    if (!name || /^\[.*\]$/.test(name)) continue; // empty "[Nome técnico N]" slots
    const years = Array.from({ length: PERSONNEL_YEARS }, (_, y) =>
      text(ws, at(1 + y, 11)),
    );
    // Rows are Gantt tasks: their ETI are summed per WP (the form's activity).
    const byWorkPackage = new Map();
    for (let row = PERSONNEL_ROWS[0]; row <= PERSONNEL_ROWS[1]; row++) {
      const task = text(ws, `B${row}`);
      if (!task || !text(ws, `${trlColumn}${row}`)) continue;
      const eti = {};
      years.forEach((year, y) => {
        const value = number(ws, at(1 + y, row));
        if (year && value) eti[year] = value;
      });
      if (!Object.keys(eti).length) continue;
      const activity = taskWorkPackage.get(normText(task)) || task;
      const entry = byWorkPackage.get(activityKey(activity)) ?? {
        activity,
        eti: {},
        contents: [],
      };
      Object.entries(eti).forEach(([year, value]) => {
        entry.eti[year] = (entry.eti[year] ?? 0) + value;
      });
      const content = text(ws, at(0, row));
      if (content && !entry.contents.includes(content)) entry.contents.push(content);
      byWorkPackage.set(activityKey(activity), entry);
    }
    const assignments = [...byWorkPackage.values()].map((entry) => ({
      activity: entry.activity,
      eti: Object.fromEntries(
        Object.entries(entry.eti).map(([year, value]) => [year, round(value, 4)]),
      ),
      content: entry.contents.join("; "),
    }));
    const ss = number(ws, at(5, 9));
    technicians.push({
      name,
      bond: text(ws, at(1, 7)),
      level: text(ws, at(3, 7)),
      critical: normText(text(ws, at(4, 7))) === "sim",
      salary: number(ws, at(2, 9)),
      ss: ss == null ? null : round(ss <= 1 ? ss * 100 : ss, 4),
      assignments,
    });
  }
  return technicians;
};

const readCosts = (wb) => {
  const ws = wb.Sheets[COSTS_SHEET];
  if (!ws?.["!ref"]) return [];
  const last = XLSX.utils.decode_range(ws["!ref"]).e.r + 1;
  const costs = [];
  for (let row = 5; row <= last; row++) {
    const entity = text(ws, `B${row}`);
    const rubric = text(ws, `K${row}`);
    if (!entity || !rubric || normText(rubric) === STAFF_RUBRIC) continue;
    const value = number(ws, `L${row}`) ?? number(ws, `G${row}`);
    if (!value) continue;
    const acquisition = dateParts(ws, `H${row}`);
    // Column I holds the year; a date there (or only in H) gives year and month.
    const yearCell = dateParts(ws, `I${row}`);
    const date = yearCell?.m ? yearCell : acquisition;
    const year = yearCell?.y ?? acquisition?.y ?? null;
    costs.push({
      entity,
      typology: text(ws, `C${row}`),
      designation: text(ws, `D${row}`),
      activity: text(ws, `J${row}`),
      rubric,
      year: year ? String(year) : "",
      month: date?.m ? String(date.m) : "",
      cost: round(value),
    });
  }
  return costs;
};

const readFinancing = (wb, entity) => {
  const ws = wb.Sheets[FINANCING_SHEET];
  if (!ws?.["!ref"]) return null;
  const last = XLSX.utils.decode_range(ws["!ref"]).e.r + 1;
  // Blocks of 23 rows, one per entity: name, header, then the 20 rubrics.
  let start = null;
  for (let row = 1; row <= last; row++) {
    if (
      entityKey(text(ws, `B${row}`)) === entityKey(entity.name) &&
      normText(text(ws, `B${row + 1}`)) === "rubricas"
    ) {
      start = row + 2;
      break;
    }
  }
  if (start == null) return null;
  return Array.from({ length: FINANCING_ROWS }, (_, i) => number(ws, `G${start + i}`));
};

export const readMeritoWorkbook = (wb, config = {}) => {
  if (!wb?.Sheets?.[CONDITIONS_SHEET] || !wb.Sheets[GANTT_SHEET]) {
    throw new Error(
      `O ficheiro não tem as folhas "${CONDITIONS_SHEET}" e "${GANTT_SHEET}".`,
    );
  }
  const tasks = readGanttTasks(wb);
  const taskWorkPackage = new Map(
    tasks.map((task) => [normText(task.designation), task.workPackage]),
  );
  const entities = readEntities(wb).map((entity) => ({
    ...entity,
    technicians: readTechnicians(wb, entity, taskWorkPackage),
    financing: readFinancing(wb, entity),
  }));
  return {
    entities,
    schedule: readSchedule(wb),
    caes: readCaes(wb),
    activities: readWorkPackages(tasks),
    costs: readCosts(wb),
  };
};

// ---------------------------------------------------------------- mapping

const NAME_NOISE = new Set(["lda", "sa", "s", "a", "unipessoal", "de", "da", "do", "e", "-"]);
const nameTokens = (name) =>
  new Set(
    normText(name)
      .replace(/[^a-z0-9 ]/g, " ")
      .split(" ")
      .filter((token) => token && !NAME_NOISE.has(token)),
  );

// Suggested beneficiary for each file entity: best shared-word match, then order.
export const suggestEntityMapping = (entities, beneficiaries) => {
  const mapping = {};
  const used = new Set();
  entities.forEach((entity) => {
    const tokens = nameTokens(entity.name);
    let best = null;
    let bestScore = 0;
    (beneficiaries ?? []).forEach((ben) => {
      if (used.has(String(ben.id))) return;
      const benTokens = nameTokens(ben.name);
      const score = [...tokens].filter(
        (token) =>
          benTokens.has(token) ||
          [...benTokens].some((b) => b.startsWith(token) || token.startsWith(b)),
      ).length;
      if (score > bestScore) {
        best = ben;
        bestScore = score;
      }
    });
    if (best) {
      mapping[entity.index] = String(best.id);
      used.add(String(best.id));
    }
  });
  return mapping;
};

// ---------------------------------------------------------------- merging

// ActivityDegree.json: the dissemination / management WPs are 03 / 04 whatever
// their TRL; the others follow their most frequent TRL ([3-4] -> 01, [5-9] -> 02).
const maturityDegree = ({ designation, maturity }) => {
  const wp = normText(designation);
  if (wp.includes("divulgacao e promocao")) return "03";
  if (/gestao d[eo] projeto/.test(wp)) return "04";
  return { "[3-4]": "01", "[5-9]": "02" }[maturity.replace(/\s/g, "")];
};

const emptySummary = () => ({ created: 0, updated: 0, replaced: 0, deleted: 0, skipped: 0 });

export const applyMerito = (formData, parsed, { sections, mapping }, jsonData, config = {}) => {
  const data = { ...(formData ?? {}) };
  const summary = Object.fromEntries(MERITO_SECTIONS.map((s) => [s.key, emptySummary()]));
  const warnings = [];
  const selected = (key) => sections.has(key);
  const beneficiaries = data.beneficiaries ?? [];
  const beneficiaryOf = (entity) =>
    beneficiaries.find((ben) => sameId(ben.id, mapping[entity.index])) ?? null;
  const mappedEntities = parsed.entities.filter((entity) => beneficiaryOf(entity));
  const mappedIds = new Set(mappedEntities.map((entity) => String(beneficiaryOf(entity).id)));
  const entityByName = new Map(parsed.entities.map((e) => [entityKey(e.name), e]));

  // Calendarização
  if (selected("schedule")) {
    const { startDate, endDate } = parsed.schedule;
    if (startDate && startDate !== data.projectStartDate) {
      data.projectStartDate = startDate;
      summary.schedule.updated++;
    }
    if (endDate && endDate !== data.projectEndDate) {
      data.projectEndDate = endDate;
      summary.schedule.updated++;
    }
  }

  const firstLocation = (benId) =>
    (data.projectLocation ?? []).find((loc) => sameId(loc.beneficiary, benId))?.id ?? "";
  // Establishments are never created or changed: team and cost rows use the
  // form's establishment with the entity's address, else the beneficiary's first.
  const entityLocation = (entity, benId) =>
    (data.projectLocation ?? []).find(
      (loc) =>
        sameId(loc.beneficiary, benId) &&
        entity.address &&
        normText(loc.address) === normText(entity.address),
    )?.id ?? firstLocation(benId);

  // CAE do projeto: the list becomes the file's (accepted) CAEs.
  if (selected("cae")) {
    const current = data.projectCAE ?? [];
    const caes = [];
    parsed.caes.forEach(({ cae, percent }) => {
      if (!(jsonData?.CAES ?? []).some((option) => option.value === cae)) {
        warnings.push(`CAE ${cae} não existe na lista de CAE do aviso.`);
        summary.cae.skipped++;
        return;
      }
      if (caes.some((row) => row.cae === cae)) return;
      const existing = current.find((row) => row.cae === cae);
      caes.push({ ...(existing ?? {}), cae, "%": percent });
      if (!existing) summary.cae.created++;
      else if (existing["%"] !== percent) summary.cae.updated++;
    });
    summary.cae.deleted = current.filter(
      (row) => !caes.some((kept) => kept.cae === row.cae),
    ).length;
    data.projectCAE = caes;
    // Main CAE: the accepted one with the highest %.
    const main = [...caes].sort((a, b) => (b["%"] ?? 0) - (a["%"] ?? 0))[0];
    data.projectMainCAE = main ? main.cae : "";
  }

  // Atividades (Gantt WPs): the list becomes the file's.
  let activities = [...(data.projectActivities ?? [])];
  const findActivity = (designation) =>
    activities.find((act) => activityKey(act.designation) === activityKey(designation));
  const removedActivityIds = new Set();
  if (selected("activities")) {
    const current = activities;
    const matched = new Set(); // indexes of current
    let id = nextId(current);
    activities = [];
    parsed.activities.forEach((source) => {
      const isInnovation = normText(source.maturity) === "inovacao";
      const nature = maturityDegree(source);
      const fields = {
        designation: source.designation,
        component: isInnovation ? "INOV" : "IDT",
        ...(source.startDate && { startDate: source.startDate }),
        ...(source.endDate && { endDate: source.endDate }),
        ...(nature && { nature }),
        activityType: isInnovation ? optionValue(jsonData?.InnovationType, source.innovation) || null : null,
      };
      const index = current.findIndex(
        (act, i) =>
          !matched.has(i) && activityKey(act.designation) === activityKey(source.designation),
      );
      if (index >= 0) {
        matched.add(index);
        activities.push({ ...current[index], ...fields });
        summary.activities.updated++;
      } else {
        activities.push({ id: id++, ...fields });
        summary.activities.created++;
      }
    });
    current.forEach((act, i) => {
      if (!matched.has(i)) removedActivityIds.add(String(act.id));
    });
    summary.activities.deleted = removedActivityIds.size;
    data.projectActivities = activities;
  }

  // Pessoal técnico: per imported beneficiary, the technicians become the file's.
  const technicians = [...(data.technicians ?? [])];
  const technicianIds = new Map(); // "entityIndex:slot" -> technician id
  const removedTechnicianIds = new Set();
  if (selected("technicians") || selected("team")) {
    const matchedTechnicians = new Set(); // technician ids
    mappedEntities.forEach((entity) => {
      const ben = beneficiaryOf(entity);
      entity.technicians.forEach((source, slot) => {
        const bond = optionValue(jsonData?.TechnicianBond, source.bond);
        const isHire = bond === HIRE_BOND;
        const free = (tech) =>
          sameId(tech.beneficiary, ben.id) && !matchedTechnicians.has(String(tech.id));
        let index = isHire
          ? technicians.findIndex((tech) => free(tech) && tech.new === "S")
          : technicians.findIndex(
              (tech) => free(tech) && normText(tech.name) === normText(source.name),
            );
        if (selected("technicians")) {
          const fields = {
            beneficiary: ben.id,
            new: isHire ? "S" : "N",
            ...(!isHire && { name: source.name }),
            ...(bond && { contractBond: bond }),
            ...(optionValue(jsonData?.TechnicianLevel, source.level) && {
              level: optionValue(jsonData?.TechnicianLevel, source.level),
            }),
            ...(source.salary != null && { salary: source.salary }),
            ...(source.ss != null && { ss: source.ss }),
            critical: source.critical ? "S" : "",
          };
          if (index >= 0) {
            technicians[index] = { ...technicians[index], ...fields };
            summary.technicians.updated++;
          } else {
            technicians.push({ id: nextId(technicians), ...fields });
            index = technicians.length - 1;
            summary.technicians.created++;
          }
        }
        if (index < 0) {
          warnings.push(`Técnico "${source.name}" (${entity.name}) não existe no formulário.`);
          return;
        }
        matchedTechnicians.add(String(technicians[index].id));
        technicianIds.set(`${entity.index}:${slot}`, technicians[index].id);
      });
    });
    if (selected("technicians")) {
      technicians.forEach((tech) => {
        if (mappedIds.has(String(tech.beneficiary)) && !matchedTechnicians.has(String(tech.id))) {
          removedTechnicianIds.add(String(tech.id));
        }
      });
      summary.technicians.deleted = removedTechnicianIds.size;
      data.technicians = technicians.filter((tech) => !removedTechnicianIds.has(String(tech.id)));
    }
  }

  // Equipa: technician x activity. Per imported beneficiary, the rows become the file's.
  if (selected("team")) {
    const team = [...(data.team ?? [])];
    const teamYears = config.teamYears ?? [];
    const touchedTeam = new Set(); // row ids matched or created
    const technicianBeneficiary = new Map(
      technicians.map((tech) => [String(tech.id), String(tech.beneficiary)]),
    );
    mappedEntities.forEach((entity) => {
      const ben = beneficiaryOf(entity);
      entity.technicians.forEach((source, slot) => {
        const technicianId = technicianIds.get(`${entity.index}:${slot}`);
        if (technicianId == null) return;
        source.assignments.forEach((assignment) => {
          const activity = findActivity(assignment.activity);
          if (!activity) {
            warnings.push(`Equipa: atividade "${assignment.activity}" não existe no formulário.`);
            summary.team.skipped++;
            return;
          }
          if (config.teamIdtOnly && activity.component !== "IDT") {
            summary.team.skipped++;
            return;
          }
          const outside = Object.keys(assignment.eti).filter((year) => !teamYears.includes(year));
          if (outside.length) {
            warnings.push(
              `Equipa: ETI de ${source.name} em ${outside.join(", ")} (fora dos anos do aviso) não foi importado.`,
            );
          }
          const fields = {
            ...Object.fromEntries(teamYears.map((year) => [year, assignment.eti[year] ?? 0])),
            ...(assignment.content && { content: assignment.content }),
            ...(config.teamComponent && { component: activity.component }),
          };
          const index = team.findIndex(
            (row) => sameId(row.technician, technicianId) && sameId(row.activity, activity.id),
          );
          if (index >= 0) {
            team[index] = {
              ...team[index],
              ...fields,
              location: team[index].location || entityLocation(entity, ben.id),
            };
            touchedTeam.add(String(team[index].id));
            summary.team.updated++;
          } else {
            const id = nextId(team);
            team.push({
              id,
              technician: technicianId,
              activity: String(activity.id),
              location: entityLocation(entity, ben.id),
              content: "",
              ...fields,
            });
            touchedTeam.add(String(id));
            summary.team.created++;
          }
        });
      });
    });
    data.team = team.filter(
      (row) =>
        touchedTeam.has(String(row.id)) ||
        !mappedIds.has(technicianBeneficiary.get(String(row.technician))),
    );
    summary.team.deleted = team.length - data.team.length;
  }

  // Team rows of deleted technicians, and the imported beneficiaries' rows of
  // deleted activities, are deleted with them (as in the ROBERTO import; the
  // other beneficiaries' rows are left without activity further down).
  if (removedActivityIds.size || removedTechnicianIds.size) {
    const team = data.team ?? [];
    const technicianBeneficiary = new Map(
      technicians.map((tech) => [String(tech.id), String(tech.beneficiary)]),
    );
    data.team = team.filter(
      (row) =>
        !removedTechnicianIds.has(String(row.technician)) &&
        !(
          removedActivityIds.has(String(row.activity)) &&
          mappedIds.has(technicianBeneficiary.get(String(row.technician)))
        ),
    );
    const count = team.length - data.team.length;
    summary.team.deleted += count;
    if (count && !selected("team")) {
      warnings.push(
        `Equipa: ${count} linha(s) de atividades ou técnicos apagados também foram apagadas.`,
      );
    }
  }

  // Custos: the file replaces the rows of the imported beneficiaries.
  if (selected("costs")) {
    const tables = { idtProjectCosts: [], inovProjectCosts: [] };
    const unknownActivities = new Set();
    parsed.costs.forEach((source) => {
      const entity = entityByName.get(entityKey(source.entity));
      const ben = entity && beneficiaryOf(entity);
      if (!ben) return;
      const typology = entityKey(source.typology);
      const isInnovation = ["investimento produtivo", "inovacao", "inov"].includes(typology);
      if (!isInnovation && !["idt", "i d"].includes(typology)) {
        warnings.push(
          `Custos: tipologia "${source.typology}" desconhecida (${source.designation}); linha ignorada.`,
        );
        summary.costs.skipped++;
        return;
      }
      const field = isInnovation ? "inovProjectCosts" : "idtProjectCosts";
      const rubricLabel = normText(source.rubric);
      const rubric = isInnovation
        ? optionValue(jsonData?.InovCostsRubric, source.rubric)
        : rubricLabel.startsWith("deslocac")
          ? TRAVEL_RUBRIC
          : rubricLabel.startsWith("estadia")
            ? STAY_RUBRIC
            : optionValue(jsonData?.IdtCostsRubric, source.rubric);
      if (!rubric) {
        warnings.push(`Custos: rubrica "${source.rubric}" não existe no aviso (${source.designation}).`);
      }
      const activity = source.activity ? findActivity(source.activity) : null;
      if (source.activity && !activity) unknownActivities.add(source.activity);
      tables[field].push({
        beneficiary: ben.id,
        location: entityLocation(entity, ben.id),
        activity: activity ? String(activity.id) : "",
        designation: source.designation,
        rubric,
        year: source.year,
        month: source.month,
        cost: source.cost,
      });
    });
    unknownActivities.forEach((name) =>
      warnings.push(`Custos: atividade "${name}" não existe no formulário (fica por preencher).`),
    );
    Object.entries(tables).forEach(([field, rows]) => {
      const kept = (data[field] ?? []).filter((row) => !mappedIds.has(String(row?.beneficiary)));
      summary.costs.replaced += (data[field] ?? []).length - kept.length;
      let id = nextId(kept);
      data[field] = [...kept, ...rows.map((row) => ({ id: id++, ...row }))];
      summary.costs.created += rows.length;
    });
  }

  // Financiamento (input rows only; totals are formulas)
  if (selected("financing")) {
    const financing = { ...(data.financing ?? {}) };
    mappedEntities.forEach((entity) => {
      if (!entity.financing) {
        warnings.push(`Financiamento: bloco de "${entity.name}" não encontrado.`);
        return;
      }
      const ben = beneficiaryOf(entity);
      const rows = Array.from({ length: FINANCING_ROWS }, (_, i) => ({
        ...((financing[ben.id] ?? [])[i] ?? {}),
      }));
      FINANCING_INPUT_ROWS.forEach((i) => {
        rows[i].al = entity.financing[i] ?? null;
      });
      financing[ben.id] = rows;
      summary.financing.updated++;
    });
    data.financing = financing;
  }

  // Other rows (other beneficiaries' team rows, costs, ...) that point to a
  // deleted activity are left without it.
  if (removedActivityIds.size) {
    Object.entries(data).forEach(([key, rows]) => {
      if (key === "projectActivities" || !Array.isArray(rows)) return;
      let count = 0;
      const next = rows.map((row) => {
        if (!row || typeof row !== "object" || !removedActivityIds.has(String(row.activity))) {
          return row;
        }
        count++;
        return { ...row, activity: "" };
      });
      if (count) {
        data[key] = next;
        warnings.push(`${count} linha(s) em "${key}" apontavam para atividades apagadas e ficaram sem atividade.`);
      }
    });
  }

  return { formData: data, summary, warnings };
};
